import { RouteResourceUpdater } from 'react-resource-router';

import {
  BranchConfig,
  BranchConfigKey,
  CheckModulesConfig,
  MergeCheckConfigurationState,
  EnabledMergeChecks,
} from './types';

/**
 * Generate a BranchConfigKey used to key a BranchConfig in a Map. Required
 * because we want a stable key for distinct BranchConfig instances with the
 * same type/identifier, which we won't get if we just use a BranchConfig as
 * the key.
 * @param branchConfig the BranchConfig to generate a key for
 * @returns a BranchConfigKey representing a BranchConfig
 */
export const makeBranchConfigKey = (
  branchConfig: BranchConfig
): BranchConfigKey => {
  return `${branchConfig.type};;${branchConfig.identifier}`;
};

/**
 * Create a RouteResourceUpdater for setting the enabled status of a single check
 * inside a Map<BranchConfig, Set<string>>, inside a {@link CheckModulesConfig}.
 *
 * The values need to be updated immutably, so we create new Set and Map instances
 * before updating.
 * @param branchConfig the BranchConfig entry to update
 * @param checkModuleId id of the check module to update
 * @param enabled value to set
 * @returns a RouteResourceUpdater<CheckModulesConfig> function to perform the update
 */
export const makeCheckEnabledResourceUpdater =
  (
    branchConfig: BranchConfig,
    checkModuleId: string,
    newState: MergeCheckConfigurationState
  ): RouteResourceUpdater<CheckModulesConfig> =>
  data => {
    const branchConfigKey = makeBranchConfigKey(branchConfig);
    const enabledMergeChecks = data.enabledChecksMap.get(branchConfigKey);
    if (!enabledMergeChecks) {
      return data;
    }

    // Create new EnabledMergeChecks object with a copy of the Map of enabled check Ids
    const updatedEnabledMergeChecks: EnabledMergeChecks = {
      ...enabledMergeChecks,
      checkIds: new Map(enabledMergeChecks.checkIds),
    };

    // Update the checkIds Map for the check that was just updated
    if (newState !== 'off') {
      updatedEnabledMergeChecks.checkIds.set(
        checkModuleId,
        newState === 'required'
      );
    } else {
      updatedEnabledMergeChecks.checkIds.delete(checkModuleId);
    }

    const updatedEnabledChecksMap = new Map(data.enabledChecksMap);
    updatedEnabledChecksMap.set(branchConfigKey, updatedEnabledMergeChecks);

    return {
      ...data,
      enabledChecksMap: updatedEnabledChecksMap,
    };
  };
